import React, { useEffect, useState, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, BookOpen, ShieldCheck } from 'lucide-react';
import { CTAButton } from './components/CTAButton';
import { LeafAccent } from './components/LeafAccent';
import { FAQItem } from './components/FAQItem';
import { LegalModal } from './components/LegalModal';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    const w = window as any;
    if (!w.fbq) {
      const n: any = w.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
    }
    w.fbq('init', '2201031127419423');
    w.fbq('track', 'PageView');
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream font-sans text-gray-800 selection:bg-gold/30" data-id="element-1">
      {/* Top Banner */}
      <div className="bg-forest-dark text-white text-center py-2 text-sm font-medium" data-id="element-2">
        Atención: Oferta especial por tiempo limitado
      </div>

      <main className="max-w-[720px] mx-auto px-5 py-10 md:py-16" data-id="element-3">
        {/* 1. HERO SECTION */}
        <header className="text-center mb-12" data-id="element-4">
          <FadeIn data-id="element-5">
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-forest-dark leading-tight mb-6" data-id="element-6">
              Recupera tu Claridad Mental de los 30s: Dile Adiós a la "Niebla"
              de la Menopausia en Solo 7 Días
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-7">
            <h2 className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-8">
              El protocolo natural de alimentación y enfoque diseñado
              específicamente para la mujer de 45 a 65+ que está cansada de
              olvidar nombres, perder el hilo de las conversaciones y vivir en
              un cansancio eterno.
            </h2>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-9">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8 bg-white p-2 border border-cream-dark" data-id="element-10">
              <img src="https://cdn.magicpatterns.com/uploads/dBaL3P5W3vgUrH2e2aFHdv/menouspausa.png" alt="Menopausia Sin Niebla - Guía Práctica en múltiples dispositivos" className="w-full h-auto rounded-xl" data-id="element-11" />
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-12">
            <CTAButton onClick={scrollToOffer} data-id="element-13" />
          </FadeIn>
        </header>

        <LeafAccent className="my-12" data-id="element-14" />

        {/* 2. OPENING HOOK */}
        <section className="mb-16" data-id="element-15">
          <FadeIn data-id="element-16">
            <p className="text-lg md:text-xl leading-relaxed mb-6 font-medium text-forest-dark" data-id="element-17">
              Entras a una habitación y, de repente, te detienes. No tienes la
              menor idea de a qué ibas.
            </p>
            <p className="text-lg leading-relaxed mb-6" data-id="element-18">
              Intentas recordar el nombre de esa persona que tienes enfrente,
              pero tu cerebro te entrega una página en blanco. Sientes que la
              mujer ágil, aguda y capaz que siempre fuiste se está desvaneciendo
              detrás de una nube espesa.
            </p>
            <p className="text-lg leading-relaxed mb-6" data-id="element-19">
              No es que estés envejeciendo prematuramente. No es que estés
              perdiendo el juicio.
            </p>
            <div className="bg-white p-6 rounded-xl border-l-4 border-gold shadow-sm" data-id="element-20">
              <p className="text-lg leading-relaxed font-semibold text-forest-dark" data-id="element-21">
                Tu cerebro está sufriendo un "incendio" invisible llamado
                neuro-inflamación, y ninguna cantidad de café va a apagarlo.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* 3. THE PROBLEM */}
        <section className="mb-16 bg-cream-dark -mx-5 px-5 py-12 md:rounded-3xl md:mx-0 md:px-10" data-id="element-22">
          <FadeIn data-id="element-23">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-6" data-id="element-24">
              El alto costo de la "niebla mental"
            </h3>
            <p className="text-lg leading-relaxed mb-6" data-id="element-25">
              Para la mujer latina, el costo de esta "niebla mental" es
              altísimo. Somos el pilar de la familia, las que gestionan las
              agendas de todos y las que resuelven los problemas en el trabajo.
            </p>
            <p className="text-lg leading-relaxed mb-6" data-id="element-26">
              Cuando tu memoria falla, tu seguridad se desploma. Vives con el
              miedo constante de cometer un error importante o de que los demás
              noten que ya no estás "al 100%".
            </p>
            <p className="text-lg leading-relaxed" data-id="element-27">
              Te han dicho que "es parte del proceso" y que debes aguantarte los
              sofocos, el insomnio y la fatiga. Han intentado venderte hormonas
              que te dan miedo o suplementos caros que no hacen nada.
            </p>
          </FadeIn>
        </section>

        {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
        <section className="mb-16" data-id="element-28">
          <FadeIn data-id="element-29">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-8 text-center" data-id="element-30">
              Por qué lo que has intentado no funciona
            </h3>
            <div className="space-y-6" data-id="element-31">
              {[{
              title: 'El café extra:',
              desc: 'Solo dispara tu cortisol, creando un círculo vicioso de ansiedad y un "crash" de energía a las 3 PM.'
            }, {
              title: 'Las vitaminas genéricas:',
              desc: 'Tu cuerpo no puede absorberlas si tu sistema digestivo está inflamado por los cambios hormonales.'
            }, {
              title: 'Esperar a que pase solo:',
              desc: 'La inflamación no tratada puede durar años, robándote la alegría de tus mejores décadas.'
            }, {
              title: 'Recetas de internet:',
              desc: 'La mayoría no entiende que el metabolismo de una mujer de 50 años no funciona igual que el de una de 20.'
            }].map((item, idx) => <div key={idx} className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-32">
                  <XCircle className="text-red-500 flex-shrink-0 mt-1" size={24} data-id="element-33" />
                  <div data-id="element-34">
                    <span className="font-bold text-forest-dark block mb-1" data-id="element-35">
                      {item.title}
                    </span>
                    <span className="text-gray-600" data-id="element-36">{item.desc}</span>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </section>

        <LeafAccent className="my-12" data-id="element-37" />

        {/* 5. THE UNIQUE MECHANISM */}
        <section className="mb-16" data-id="element-38">
          <FadeIn data-id="element-39">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-6" data-id="element-40">
              La solución no es "forzar" a tu cerebro a trabajar más, sino
              limpiar el filtro.
            </h3>
            <p className="text-lg leading-relaxed mb-6" data-id="element-41">
              Presentamos el{' '}
              <strong className="text-forest-dark" data-id="element-42">
                Protocolo de Enfriamiento Metabólico
              </strong>
              .
            </p>
            <p className="text-lg leading-relaxed mb-6" data-id="element-43">
              Este sistema utiliza combinaciones específicas de alimentos y
              rituales de tiempo que "apagan" la inflamación cerebral. Al bajar
              la insulina y estabilizar el cortisol, tus neuronas vuelven a
              comunicarse a la velocidad de la luz.
            </p>
            <div className="bg-forest text-white p-6 rounded-xl shadow-md text-center text-lg font-medium italic" data-id="element-44">
              "Es como reiniciar una computadora que estaba congelada."
            </div>
          </FadeIn>
        </section>

        <div className="mb-16" data-id="element-45">
          <FadeIn data-id="element-46">
            <CTAButton onClick={scrollToOffer} data-id="element-47" />
          </FadeIn>
        </div>

        {/* 6. WHAT YOU GET */}
        <section className="mb-16 bg-white rounded-3xl p-6 md:p-10 shadow-lg border border-cream-dark" data-id="element-48">
          <FadeIn data-id="element-49">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-8 text-center" data-id="element-50">
              Lo que obtienes hoy
            </h3>
            <div className="space-y-6" data-id="element-51">
              {[{
              title: 'La Guía Maestra "Menopausia Sin Niebla" (PDF):',
              desc: 'El mapa completo para entender y vencer la niebla mental.'
            }, {
              title: 'El Plan de Rescate de 7 Días:',
              desc: 'Tu menú diario detallado para recuperar el foco de inmediato.'
            }, {
              title: 'Lista de Superfoods Anti-Inflamatorios:',
              desc: 'Los ingredientes exactos que necesitas (y los que debes tirar hoy mismo).'
            }, {
              title: 'Ritual Nocturno de "Limpieza Cerebral":',
              desc: 'La técnica de 5 minutos para asegurar que mañana te despiertes con energía.'
            }, {
              title: 'Hoja de Seguimiento de Claridad:',
              desc: 'Para que veas cómo tu memoria mejora cada 24 horas.'
            }].map((item, idx) => <div key={idx} className="flex items-start gap-4" data-id="element-52">
                  <CheckCircle2 className="text-forest flex-shrink-0 mt-1" size={24} data-id="element-53" />
                  <div data-id="element-54">
                    <span className="font-bold text-forest-dark block mb-1" data-id="element-55">
                      {item.title}
                    </span>
                    <span className="text-gray-600" data-id="element-56">{item.desc}</span>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </section>

        {/* 7. WHO THIS IS FOR / NOT FOR */}
        <section className="mb-16 grid md:grid-cols-2 gap-8" data-id="element-57">
          <FadeIn data-id="element-58">
            <div className="bg-cream-dark p-6 rounded-2xl h-full border border-gold/20" data-id="element-59">
              <h4 className="font-serif text-xl font-bold text-forest-dark mb-6 flex items-center gap-2" data-id="element-60">
                <CheckCircle2 className="text-forest" data-id="element-61" />
                Esto ES para ti si:
              </h4>
              <ul className="space-y-4" data-id="element-62">
                <li className="flex items-start gap-3" data-id="element-63">
                  <div className="w-1.5 h-1.5 rounded-full bg-forest mt-2.5 flex-shrink-0" data-id="element-64" />
                  <span className="text-gray-700" data-id="element-65">
                    Mujeres latinas de 40 a 65+ que trabajan o lideran familias.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-66">
                  <div className="w-1.5 h-1.5 rounded-full bg-forest mt-2.5 flex-shrink-0" data-id="element-67" />
                  <span className="text-gray-700" data-id="element-68">
                    Quienes sienten que el cansancio mental les está robando su
                    calidad de vida.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-69">
                  <div className="w-1.5 h-1.5 rounded-full bg-forest mt-2.5 flex-shrink-0" data-id="element-70" />
                  <span className="text-gray-700" data-id="element-71">
                    Mujeres que prefieren soluciones naturales, lógicas y
                    basadas en comida real.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-72">
                  <div className="w-1.5 h-1.5 rounded-full bg-forest mt-2.5 flex-shrink-0" data-id="element-73" />
                  <span className="text-gray-700 font-medium" data-id="element-74">
                    Sientes que tu memoria te está fallando y quieres frenarlo
                    ¡YA!
                  </span>
                </li>
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-75">
            <div className="bg-white p-6 rounded-2xl h-full border border-gray-200" data-id="element-76">
              <h4 className="font-serif text-xl font-bold text-gray-800 mb-6 flex items-center gap-2" data-id="element-77">
                <XCircle className="text-red-500" data-id="element-78" />
                Esto NO es para ti si:
              </h4>
              <ul className="space-y-4" data-id="element-79">
                <li className="flex items-start gap-3" data-id="element-80">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2.5 flex-shrink-0" data-id="element-81" />
                  <span className="text-gray-600" data-id="element-82">
                    Quienes buscan una pastilla mágica que no requiera cambiar
                    ni un hábito.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-83">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2.5 flex-shrink-0" data-id="element-84" />
                  <span className="text-gray-600" data-id="element-85">
                    Personas que no están abiertas a probar nuevos ingredientes
                    naturales.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-86">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2.5 flex-shrink-0" data-id="element-87" />
                  <span className="text-gray-600" data-id="element-88">
                    Hombres o mujeres que no estén en la etapa de transición
                    hormonal.
                  </span>
                </li>
              </ul>
            </div>
          </FadeIn>
        </section>

        <LeafAccent className="my-12" data-id="element-89" />

        {/* 8. HOW IT WORKS */}
        <section className="mb-16" data-id="element-90">
          <FadeIn data-id="element-91">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-10 text-center" data-id="element-92">
              Cómo funciona
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-forest/30 before:to-transparent" data-id="element-93">
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-94">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-forest text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-95">
                  <BookOpen size={20} data-id="element-96" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-97">
                  <h4 className="font-bold text-forest-dark mb-1" data-id="element-98">
                    Acceso Inmediato
                  </h4>
                  <p className="text-gray-600 text-sm md:text-base" data-id="element-99">
                    Una vez completes tu pago, recibes el PDF en tu correo en
                    segundos.
                  </p>
                </div>
              </div>

              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-100">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-forest text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-101">
                  <Clock size={20} data-id="element-102" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-103">
                  <h4 className="font-bold text-forest-dark mb-1" data-id="element-104">
                    Implementación de 7 Días
                  </h4>
                  <p className="text-gray-600 text-sm md:text-base" data-id="element-105">
                    Sigue el plan de alimentación paso a paso.
                  </p>
                </div>
              </div>

              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-106">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-forest text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-107">
                  <CheckCircle2 size={20} data-id="element-108" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-109">
                  <h4 className="font-bold text-forest-dark mb-1" data-id="element-110">
                    Claridad Total
                  </h4>
                  <p className="text-gray-600 text-sm md:text-base" data-id="element-111">
                    Nota cómo las palabras regresan, la energía sube y la nube
                    desaparece.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* 9. PROOF / BELIEVABILITY */}
        <section className="mb-16 bg-forest-dark text-white -mx-5 px-5 py-12 md:rounded-3xl md:mx-0 md:px-10 text-center" data-id="element-112">
          <FadeIn data-id="element-113">
            <ShieldCheck className="mx-auto mb-6 text-gold" size={48} data-id="element-114" />
            <p className="text-lg md:text-xl leading-relaxed mb-6 font-serif" data-id="element-115">
              Este sistema no se basa en teorías abstractas, sino en cómo el
              cuerpo procesa los nutrientes durante la caída del estrógeno.
            </p>
            <p className="text-xl md:text-2xl font-bold text-gold" data-id="element-116">
              Al cuarto día, el 85% de las mujeres reportan que la sensación de
              "cabeza pesada" desaparece por completo, permitiéndoles terminar
              su día con la misma energía con la que lo empezaron.
            </p>
          </FadeIn>
        </section>

        {/* 10. FAQ */}
        <section className="mb-16" data-id="element-117">
          <FadeIn data-id="element-118">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-8 text-center" data-id="element-119">
              Preguntas Frecuentes
            </h3>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-120">
              <FAQItem question="¿Necesito comprar ingredientes caros?" answer="No, la mayoría están en tu supermercado local o en tu propia despensa." data-id="element-121" />
              <FAQItem question="¿Es difícil de seguir?" answer="Está diseñado para mujeres ocupadas. No pasarás horas en la cocina." data-id="element-122" />
              <FAQItem question="¿Puedo leerlo en mi celular?" answer="Sí, el PDF está optimizado para dispositivos móviles, tablets y computadoras." data-id="element-123" />
              <FAQItem question="¿Hay garantía?" answer="Tu satisfacción es lo primero. Si en 7 días no sientes más claridad, nuestro soporte te ayudará." data-id="element-124" />
            </div>
          </FadeIn>
        </section>

        {/* 11. OFFER + PRICE BLOCK */}
        <section id="offer-section" className="mb-12" data-id="element-125">
          <FadeIn data-id="element-126">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-gold relative overflow-hidden" data-id="element-127">
              {/* Decorative background element */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-128"></div>

              <div className="relative z-10 text-center" data-id="element-129">
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-forest-dark mb-6" data-id="element-130">
                  Tu agudeza mental es tu activo más valioso.
                </h3>
                <p className="text-lg text-gray-700 mb-8" data-id="element-131">
                  No dejes que la menopausia te robe tu confianza. Obtén hoy el
                  protocolo completo que ha devuelto la claridad a cientos de
                  mujeres.
                </p>

                <div className="bg-cream-dark rounded-2xl p-6 mb-8 inline-block w-full max-w-md border border-gold/30" data-id="element-132">
                  <p className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-2" data-id="element-133">
                    Precio Especial Hoy
                  </p>
                  <div className="flex justify-center items-baseline gap-3 mb-2" data-id="element-134">
                    <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-135">
                      $27
                    </span>
                    <span className="text-5xl font-bold text-forest-dark" data-id="element-136">
                      $5.99
                    </span>
                    <span className="text-xl text-gray-600 font-medium" data-id="element-137">
                      USD
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-138">
                    Pago único, acceso de por vida
                  </p>
                </div>

                <CTAButton onClick={() => {
                const w = window as any;
                if (w.fbq) {
                  w.fbq('track', 'AddToCart', {
                    content_name: 'Menopausia Sin Niebla',
                    content_type: 'product',
                    value: 5.99,
                    currency: 'USD'
                  });
                }
                window.open('https://bienestarypaz.com//cart/46695173324956:1', '_blank');
              }} className="text-xl py-5 shadow-gold/20 shadow-xl" data-id="element-139" />

                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-140">
                  <ShieldCheck size={16} className="text-forest" data-id="element-141" />
                  <span data-id="element-142">Pago 100% seguro y encriptado</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        <footer className="text-center text-gray-500 text-sm pb-8" data-id="element-143">
          <p data-id="element-144">
            © {new Date().getFullYear()} Menopausia Sin Niebla. Todos los
            derechos reservados.
          </p>
          <p className="mt-2 text-xs" data-id="element-145">
            Este producto no sustituye el consejo médico profesional.
          </p>
          <div className="mt-4 flex justify-center gap-4" data-id="element-146">
            <button onClick={() => setLegalModal('privacy')} className="underline hover:text-forest transition-colors" data-id="element-147">
              Política de Privacidad
            </button>
            <span data-id="element-148">·</span>
            <button onClick={() => setLegalModal('terms')} className="underline hover:text-forest transition-colors" data-id="element-149">
              Términos y Condiciones
            </button>
          </div>
        </footer>
      </main>

      <LegalModal isOpen={legalModal === 'privacy'} onClose={() => setLegalModal(null)} title="Política de Privacidad" data-id="element-150">
        <p data-id="element-151">
          <strong data-id="element-152">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-153">
          En Menopausia Sin Niebla, respetamos y protegemos tu privacidad. Esta
          política describe cómo recopilamos, usamos y protegemos tu información
          personal.
        </p>
        <p data-id="element-154">
          <strong data-id="element-155">Información que recopilamos:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-156">
          <li data-id="element-157">
            Nombre y dirección de correo electrónico al realizar tu compra.
          </li>
          <li data-id="element-158">
            Información de pago procesada de forma segura por nuestro proveedor
            de pagos (no almacenamos datos de tarjetas).
          </li>
          <li data-id="element-159">Datos de navegación anónimos para mejorar nuestro servicio.</li>
        </ul>
        <p data-id="element-160">
          <strong data-id="element-161">Uso de la información:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-162">
          <li data-id="element-163">Entregar el producto digital adquirido.</li>
          <li data-id="element-164">Enviar comunicaciones relacionadas con tu compra.</li>
          <li data-id="element-165">Mejorar nuestros productos y servicios.</li>
        </ul>
        <p data-id="element-166">
          <strong data-id="element-167">Protección de datos:</strong> Utilizamos medidas de seguridad
          estándar de la industria para proteger tu información personal.
        </p>
        <p data-id="element-168">
          <strong data-id="element-169">Tus derechos:</strong> Puedes solicitar acceso, corrección o
          eliminación de tus datos personales en cualquier momento
          contactándonos por correo electrónico.
        </p>
        <p data-id="element-170">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros sin tu consentimiento.
        </p>
      </LegalModal>

      <LegalModal isOpen={legalModal === 'terms'} onClose={() => setLegalModal(null)} title="Términos y Condiciones" data-id="element-171">
        <p data-id="element-172">
          <strong data-id="element-173">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-174">
          Al adquirir el producto "Menopausia Sin Niebla", aceptas los
          siguientes términos y condiciones:
        </p>
        <p data-id="element-175">
          <strong data-id="element-176">Producto digital:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-177">
          <li data-id="element-178">
            Recibirás acceso inmediato al contenido digital (PDF) tras completar
            tu pago.
          </li>
          <li data-id="element-179">El acceso es personal e intransferible.</li>
          <li data-id="element-180">
            No está permitida la redistribución, copia o reventa del material.
          </li>
        </ul>
        <p data-id="element-181">
          <strong data-id="element-182">Naturaleza informativa:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-183">
          <li data-id="element-184">
            Este producto tiene fines informativos y educativos únicamente.
          </li>
          <li data-id="element-185">
            No sustituye el diagnóstico, tratamiento o consejo de un profesional
            médico.
          </li>
          <li data-id="element-186">
            Consulta siempre a tu médico antes de realizar cambios en tu
            alimentación o estilo de vida.
          </li>
        </ul>
        <p data-id="element-187">
          <strong data-id="element-188">Política de satisfacción:</strong> Si en 7 días no sientes
          mejoras en tu claridad mental, nuestro equipo de soporte te asistirá
          para resolver cualquier duda.
        </p>
        <p data-id="element-189">
          <strong data-id="element-190">Limitación de responsabilidad:</strong> Los resultados pueden
          variar de persona a persona. No garantizamos resultados específicos ya
          que dependen de factores individuales.
        </p>
        <p data-id="element-191">
          <strong data-id="element-192">Propiedad intelectual:</strong> Todo el contenido del producto
          está protegido por derechos de autor. Queda prohibida su reproducción
          total o parcial sin autorización.
        </p>
      </LegalModal>
    </div>;
}